import { useCanAuthorContent, useCurrentUser } from '@/api/hooks';
import { useAlfieCapabilities } from '@/api/use-alfie-capabilities';
import CachedAvatar from '@/components/ui/CachedAvatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ThemeToggle, ThemeToggleSimple } from '@/components/ui/ThemeToggle';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useOrgRole } from '@/hooks/use-org-role';
import { isRoadmapEnabled, isSpeediesEnabled, isXeroEnabled } from '@/lib/feature-flags';
import { readLastFinanceView } from '@/lib/finance-nav-memory';
import { useNotificationRealtime, useUserChannel, useCommunityProfilesChannel } from '@/api/community-realtime';
import { useUnreadCounts } from '@/api/community-hooks';
import { useCommunityUnread } from '@/hooks/use-community-unread';
import { useDocumentTitleUnread } from '@/lib/notifications/title';
import { usePushSubscription } from '@/lib/notifications/push-subscription';
import { DunningWarning } from '@/components/billing/DunningWarning';
import { EnableNotificationsBanner } from '@/components/notifications/EnableNotificationsBanner';
import { InstallPwaBanner } from '@/components/notifications/InstallPwaBanner';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import type { NotificationPreferencesData } from '@/api/client';
import {
  NavIconClose,
  NavIconMenu,
  NavIconPanelLeft,
  NavIconSettings,
} from '@/components/icons/NavIcons';
import {
  ALFIE_ITEM,
  activeModuleFor,
  buildNavItems,
  FINANCE_ITEM,
  type NavModule,
  STUB_MODULES,
} from '@/components/layout/nav-items';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'framer-motion';
import { Lock, LogOut, Sparkles } from 'lucide-react';
import AlfieWidget from '@/components/alfie-widget/AlfieWidget';
import HearthstoneIcon from '@/components/icons/HearthstoneIcon';
import { markNavSource } from '@/lib/tracking/nav-source';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SpeedyPlayerProvider } from '@/components/speedy/SpeedyPlayerProvider';

// ─── Layout widths (from v4 Design System V3.1) ─────────────────────────────
const WIDTH_EXPANDED = 240;
const WIDTH_COLLAPSED = 64;

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * A dropdown that opens on a plain click as well as on a pointer.
 *
 * Radix's `DropdownMenuTrigger` opens on `pointerdown` and on Enter/Space keydown, and does
 * nothing with `click`. A click with no pointer behind it — a synthesised one from assistive
 * tech, an automation driver, a test — therefore never opened the account menu (QA Q11, 14 Sep
 * 2026). This adds the missing path without double-toggling: a real pointer press is recorded,
 * and the click that follows it is the SAME gesture, so it is ignored. The keyboard path is
 * untouched, because Radix calls `preventDefault()` on the Enter/Space keydown and no click is
 * ever generated.
 *
 * The guard is a timestamp rather than a flag: a pointer press that never becomes a click (a
 * drag off the button) must not swallow the next real one.
 */
const POINTER_CLICK_MS = 500;

function useClickToOpen() {
  const [open, setOpen] = useState(false);
  const lastPointerAt = useRef(0);
  const triggerProps = {
    onPointerDown: () => {
      lastPointerAt.current = Date.now();
    },
    onClick: () => {
      if (Date.now() - lastPointerAt.current < POINTER_CLICK_MS) return;
      setOpen((wasOpen) => !wasOpen);
    },
  };
  return { open, onOpenChange: setOpen, triggerProps };
}

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const accountMenu = useClickToOpen();
  const accountMenuMobile = useClickToOpen();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { data: profile } = useCurrentUser();
  const { canAuthor } = useCanAuthorContent();
  const { isAdvisor } = useOrgRole();
  const { hasAlfieChat, hasCommunity, isFree, isLoading: capsLoading, isError: capsError } = useAlfieCapabilities();
  // Sidebar expanded state — persisted to localStorage via voice settings key
  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem('prometheus-voice-settings');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.sidebarExpanded ?? true;
      }
    } catch { /* ignore */ }
    return true;
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  // Track whether sidebar was collapsed BY the user (not by Alfie panel)
  const expandedBeforeAlfie = useRef(expanded);
  const collapsedByAlfie = useRef(false);

  // Persist user-commanded sidebar state (NOT Alfie-triggered collapses)
  const setExpandedByUser = useCallback((value: boolean) => {
    setExpanded(value);
    try {
      const stored = localStorage.getItem('prometheus-voice-settings');
      const settings = stored ? JSON.parse(stored) : {};
      settings.sidebarExpanded = value;
      localStorage.setItem('prometheus-voice-settings', JSON.stringify(settings));
    } catch { /* ignore */ }
  }, []);

  // On the /alfie route the Alfie conversation-list sidebar becomes the
  // primary navigation, so collapse the main nav to give Alfie full width.
  // Reuses the same non-persisted collapsedByAlfie pattern the side panel had
  // so leaving /alfie restores whatever state the user had before.
  const onAlfieRoute = location.pathname.toLowerCase().startsWith('/alfie');
  // Admin is its own workspace with its own sidebar (routes/admin.tsx), so the
  // app nav disappears entirely there rather than stacking two expanded
  // sidebars side by side. The admin nav's "Back to Home" is the way out on
  // desktop; the mobile header/drawer stays, since it is the only mobile nav.
  const onAdminRoute = location.pathname.startsWith('/admin');
  // Onboarding renders inside the app shell, so the nav is on screen while the
  // wizard runs. The root guard already bounces anyone with a null
  // `onboarding_completed_at` straight back here, which meant a click on a nav
  // item navigated away and then snapped back. Make them inert instead — the
  // items stay visible (so the viewer can see what's coming) but do nothing.
  const onOnboardingRoute = location.pathname.startsWith('/onboarding');
  // The floating Alfie widget: not on /alfie (that page IS Alfie), not for
  // confirmed-Free viewers (no alfie_chat entitlement, see the mount site),
  // and not during onboarding, a task-critical wizard where a chat launcher
  // over its sticky Next bar is noise.
  const showAlfieWidget = !onAlfieRoute && !onOnboardingRoute && !isFree;
  const onFullBleedRoute = onAlfieRoute
    // Admin owns its scroll: the shell keeps the admin sidebar pinned while
    // only the content column scrolls. If the layout's padded scroller wrapped
    // it instead, scrolling a long admin page would move the sidebar too.
    || onAdminRoute
    // The /community feed + chat manage their own internal scroll (full-bleed).
    // Detail pages under /community (e.g. a single post) are normal scrollable
    // pages and must use the layout's scroll container.
    || (location.pathname.startsWith('/community')
      && !location.pathname.startsWith('/community/post/'))
    || location.pathname.startsWith('/events')
    // /recordings (library + detail) owns its scroll the same way /events
    // does: the pill bar stays pinned while only the content column scrolls.
    // Note this does NOT match the legacy singular /recording/$id route.
    || location.pathname.startsWith('/recordings')
    // Checkout owns its scroll so its order bar can pin flush to the bottom,
    // full-width — the standard px/py wrapper would float it on a gutter.
    || location.pathname.startsWith('/checkout');
  // Finance gets the same width treatment (Tim, 13 Oct 2026): the cockpit is wide, so opening it
  // collapses the main menu, and leaving finance restores whatever the user had.
  const onFinanceRoute = location.pathname.startsWith('/finance');
  const collapseNavForRoute = onAlfieRoute || onFinanceRoute;
  useEffect(() => {
    if (collapseNavForRoute) {
      if (expanded && !collapsedByAlfie.current) {
        expandedBeforeAlfie.current = expanded;
        collapsedByAlfie.current = true;
        setExpanded(false); // non-persisted
      }
    } else if (collapsedByAlfie.current) {
      setExpanded(expandedBeforeAlfie.current);
      collapsedByAlfie.current = false;
    }
  }, [collapseNavForRoute]);

  // Lock the document scroll for routes that own their own viewport (chat,
  // alfie). Without this, iOS Safari rubber-band-scrolls the body when the
  // inner scroll container reaches its edge, dragging the top nav tabs out
  // of view. overscroll-contain on the inner scroller alone doesn't help —
  // it only prevents scroll *chaining*, not the body being scrollable at
  // all. Gated via a body data-attribute so home / dashboard / privacy
  // routes keep their normal page scrolling.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (onFullBleedRoute) {
      document.body.setAttribute('data-fullbleed', 'true');
      document.documentElement.setAttribute('data-fullbleed', 'true');
      return () => {
        document.body.removeAttribute('data-fullbleed');
        document.documentElement.removeAttribute('data-fullbleed');
      };
    }
  }, [onFullBleedRoute]);

  // Standalone (installed PWA) detection for CSS. iOS does NOT reliably match the
  // `@media (display-mode: standalone)` query — it exposes `navigator.standalone`
  // instead (see InstallPwaBanner / push-subscription, which check both). So set an
  // attribute JS can compute correctly and CSS can target.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const standalone = window.matchMedia?.('(display-mode: standalone)').matches
      || (window.navigator as any).standalone === true;
    document.documentElement.setAttribute('data-standalone', standalone ? 'true' : 'false');
  }, []);

  // Phase 5: App-wide realtime subscriptions
  useNotificationRealtime();
  useUserChannel(); // conversation.updated → sidebar cache + toast notifications
  useCommunityProfilesChannel(); // profile.updated → instant name/avatar refresh everywhere

  // Slack-style document title + Web Push subscription sync
  useDocumentTitleUnread();
  usePushSubscription();

  // Unread counts for Community nav badges — split by type, respect preferences
  const queryClient = useQueryClient();
  const { data: unreadCounts } = useUnreadCounts();
  const prefs = queryClient.getQueryData<NotificationPreferencesData>(['notificationPreferences']);

  // Badges show for ALL unread including muted — user wants to see activity "from afar".
  // Muted channels only suppress TOAST popups (see community-realtime.ts useUserChannel toast gate).
  const dmUnread = useMemo(() => {
    if (prefs && !prefs.direct_messages) return 0;
    return unreadCounts
      ?.filter((c) => c.type === 'dm')
      .reduce((sum, c) => sum + c.count, 0) ?? 0;
  }, [unreadCounts, prefs]);

  // Home's community card shows the same number, so the derivation lives in one hook.
  const communityUnread = useCommunityUnread();

  // Build navigation with Author module for authorized users. The finance-only
  // advisor is exclusive: Finance is their ENTIRE menu, nothing else renders.
  // `buildNavItems` holds the rules and its own tests. The flags are read here, at render, from
  // the cache the root loader warms: "Tracking" becomes the finance cockpit when Xero is live,
  // and "Roadmap" exists only while the Prime Score roadmap is live for this viewer, because
  // its route redirects to /diagnostic/results when the flag is off.
  const navItems = useMemo(
    () =>
      buildNavItems({
        isAdvisor,
        canAuthor,
        xeroOn: isXeroEnabled(),
        roadmapOn: isRoadmapEnabled(),
        speediesOn: isSpeediesEnabled(),
      }),
    [canAuthor, isAdvisor],
  );

  // An advisor who lands on (or deep-links to) anything but Finance is bounced
  // back — the nav hides other sections, this stops a typed URL reaching them.
  // Belt to the server's own per-feature gates (the figures gate on
  // financials[read]; content gates deny an advisor anyway), not the boundary.
  useEffect(() => {
    if (!isAdvisor) return;
    const p = location.pathname.toLowerCase();
    if (p !== '/finance' && !p.startsWith('/finance/') && !p.startsWith('/settings')) {
      navigate({ to: '/finance', replace: true });
    }
  }, [isAdvisor, location.pathname, navigate]);

  const activeModule = activeModuleFor(location.pathname, navItems);
  const sidebarWidth = onAdminRoute ? 0 : expanded ? WIDTH_EXPANDED : WIDTH_COLLAPSED;

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate({ to: '/login' });
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // ─── Render helpers ──────────────────────────────────────────────────────

  // `forceExpanded` overrides the outer `expanded` state for contexts where
  // we always want labels visible regardless of the desktop sidebar state —
  // specifically the mobile drawer, which is a full-nav drawer and must
  // show item labels even when the desktop sidebar is collapsed (which it
  // is by default on /alfie routes).
  const renderNavItem = (item: NavModule, isAlfie = false, forceExpanded?: boolean) => {
    const showLabels = forceExpanded ?? expanded;
    const Icon = item.icon;
    const isActive = activeModule === item.id;
    // The unread badges sit on the item that opens the chat view: Chat when Speedies are on,
    // Community otherwise. Exactly one item carries them.
    const isCommunity = item.id === (isSpeediesEnabled() ? 'chat' : 'community');
    // Tracking is a stub until the Xero integration is live; once on, it points
    // at the finance cockpit and renders as a full (non-dimmed) module.
    const isStub =
      STUB_MODULES.has(item.id) && !(item.id === 'tracking' && isXeroEnabled());
    // Finance entry: restore the builder's last finance view (Decision 3). Client-
    // only read at click time — never in a loader — so it can't crash SSR or loop.
    const isFinanceEntry = item.id === 'tracking' && isXeroEnabled();
    // Free plans don't include Alfie chat / community — grey the item and route a
    // click to checkout instead of the (guarded) destination. Only lock on a
    // settled, SUCCESSFUL read; a read error is unknown, not locked.
    const locked = !capsLoading && !capsError && (
      (item.id === 'alfie' && !hasAlfieChat) ||
      ((item.id === 'community' || item.id === 'chat') && !hasCommunity)
    );

    return (
      <Link
        key={item.id}
        to={locked ? '/checkout' : item.url}
        title={
          onOnboardingRoute
            ? `${item.label} — available once onboarding is finished`
            : locked
              ? `${item.label} — upgrade to unlock`
              : item.label
        }
        aria-label={
          onOnboardingRoute
            ? `${item.label}, unavailable until onboarding is complete.`
            : locked
              ? `${item.label}, locked. Upgrade to unlock.`
              : undefined
        }
        aria-disabled={onOnboardingRoute || undefined}
        tabIndex={onOnboardingRoute ? -1 : undefined}
        onClick={
          onOnboardingRoute
            ? (e) => e.preventDefault()
            : isFinanceEntry
              ? (e) => {
                  e.preventDefault();
                  markNavSource(forceExpanded ? 'drawer' : 'menu');
                  navigate({ to: readLastFinanceView() });
                }
              : () => markNavSource(forceExpanded ? 'drawer' : 'menu')
        }
        className={[
          'flex items-center rounded-lg transition-all duration-150 min-h-[40px] relative',
          showLabels ? 'gap-2.5 px-2.5 py-2' : 'justify-center px-0 py-2.5',
          isAlfie
            ? isActive
              ? 'bg-[hsl(var(--brand-orange))]/15 text-[hsl(var(--brand-orange))] border border-[hsl(var(--brand-orange))]/25'
              : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-foreground/10 border border-sidebar-border'
            : isActive
              ? 'bg-[hsl(var(--brand-orange))]/20 text-[hsl(var(--brand-orange))]'
              : 'text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-foreground/10',
          isAlfie ? 'mb-2' : '',
          isStub && !isActive ? 'opacity-35' : '',
          locked ? 'opacity-50' : '',
          onOnboardingRoute ? 'opacity-50 cursor-default hover:bg-transparent' : '',
        ].join(' ')}
      >
        <div className="relative flex-shrink-0">
          <Icon className="w-5 h-5" />
          {/* Collapsed mode: dot indicator when any unread */}
          {!locked && isCommunity && !showLabels && (dmUnread + communityUnread) > 0 && (
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[hsl(var(--brand-orange))] border-2 border-card" />
          )}
        </div>
        {showLabels && (
          <>
            <span className={`text-[13px] whitespace-nowrap flex-1 ${isActive ? 'font-semibold' : 'font-normal'}`}>
              {item.label}
            </span>
            {locked && (
              <Lock className="ml-auto h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
            )}
            {/* Expanded mode: DM badge (blue) + Community badge (orange) */}
            {!locked && isCommunity && (
              <span className="ml-auto flex items-center gap-1">
                {dmUnread > 0 && (
                  <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-info text-info-foreground text-[11px] font-bold flex items-center justify-center" title="Unread DMs">
                    {dmUnread > 99 ? '99+' : dmUnread}
                  </span>
                )}
                {communityUnread > 0 && (
                  <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[hsl(var(--brand-orange))] text-white text-[11px] font-bold flex items-center justify-center" title="Unread community">
                    {communityUnread > 99 ? '99+' : communityUnread}
                  </span>
                )}
              </span>
            )}
          </>
        )}
      </Link>
    );
  };

  const renderSidebarContent = () => (
    <>
      {/* Logo row — collapse control lives here, top-right, icon-only
          (sidebar convention: Notion/Linear-style panel toggle by the
          wordmark, not a labelled row in the utility cluster). Collapsed
          state keeps the slot height and centers the expand toggle in it. */}
      {expanded ? (
        <div className="flex items-center gap-2.5 px-2.5 py-2 mb-3">
          <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
            <HearthstoneIcon size={28} variant="default" />
          </div>
          <span className="font-serif italic text-lg text-sidebar-foreground tracking-tight flex-1">ALFIE AI</span>
          <button
            onClick={() => setExpandedByUser(false)}
            title="Collapse sidebar"
            aria-label="Collapse sidebar"
            className="p-1.5 rounded-lg text-sidebar-foreground/40 hover:text-sidebar-foreground/70 hover:bg-sidebar-foreground/10 transition-colors"
          >
            <NavIconPanelLeft className="w-[18px] h-[18px]" />
          </button>
        </div>
      ) : (
        <div className="flex justify-center py-2 mb-3">
          <button
            onClick={() => setExpandedByUser(true)}
            title="Expand sidebar"
            aria-label="Expand sidebar"
            className="p-1.5 rounded-lg text-sidebar-foreground/40 hover:text-sidebar-foreground/70 hover:bg-sidebar-foreground/10 transition-colors"
          >
            <NavIconPanelLeft className="w-[18px] h-[18px]" />
          </button>
        </div>
      )}

      {/* ALFIE Chat — navigates to full page */}
      {!isAdvisor && renderNavItem(ALFIE_ITEM, true)}

      {/* Divider */}
      <div className="h-px bg-sidebar-border mx-2 my-1" />

      {/* Main nav */}
      <nav className="flex-1 min-h-0 flex flex-col gap-0.5 overflow-y-auto">
        {navItems.map((item) => renderNavItem(item))}
      </nav>

      {/* Free plan upgrade — one quiet, always-available entry. Only for Free
          orgs; paid plans never render it. */}
      {isFree && (
        <Link
          to="/checkout"
          title="Upgrade your plan"
          aria-label="Upgrade your plan"
          className={[
            'mb-1 flex items-center rounded-lg border transition-colors duration-150',
            'border-[hsl(var(--primary)/0.35)] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.12)]',
            expanded ? 'gap-2.5 px-2.5 py-2' : 'justify-center px-0 py-2.5',
          ].join(' ')}
        >
          <Sparkles className="h-[18px] w-[18px] flex-shrink-0" aria-hidden />
          {expanded && (
            <span className="flex min-w-0 flex-col leading-tight">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-sidebar-foreground/70">
                Free plan
              </span>
              <span className="text-xs font-medium">Upgrade</span>
            </span>
          )}
        </Link>
      )}

      {/* Notification bell — the persistent notification center. Bottom-left
          is a stable corner on every route (full-bleed included); the popover
          opens to the right of the rail. The collapse toggle lives in the
          logo row at the top. */}
      <NotificationBell variant="sidebar" expanded={expanded} />

      {/* Divider */}
      <div className="h-px bg-sidebar-border mx-2 my-1" />

      {/* Theme toggle */}
      <div className={`flex items-center ${expanded ? 'px-2.5 py-1.5' : 'justify-center py-1.5'}`}>
        {expanded ? (
          <div className="flex items-center justify-between w-full">
            <span className="text-[11px] text-sidebar-foreground/70 font-medium">Theme</span>
            <ThemeToggle size="sm" />
          </div>
        ) : (
          <ThemeToggleSimple />
        )}
      </div>

      {/* Account (Settings lives inside the dropdown) */}
      <DropdownMenu open={accountMenu.open} onOpenChange={accountMenu.onOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            {...accountMenu.triggerProps}
            aria-label="User menu"
            title="Account"
            className={[
              'flex items-center rounded-lg transition-all duration-150 min-h-[40px] text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-foreground/10 outline-none',
              expanded ? 'gap-2.5 px-2.5 py-2' : 'justify-center px-0 py-2.5',
            ].join(' ')}
          >
            <CachedAvatar
              userId={user?.id}
              url={profile?.avatar_url ?? user?.user_metadata?.avatar_url ?? null}
              name={user?.user_metadata?.full_name ?? user?.email ?? null}
              className="h-6 w-6 flex-shrink-0 rounded-full border border-sidebar-border"
              fallbackClassName="bg-[hsl(var(--brand-orange))]/20 text-[hsl(var(--brand-orange))] text-[10px] uppercase"
            />
            {expanded && (
              <span className="text-xs whitespace-nowrap truncate">Account</span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="right" className="w-56 ml-2">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">
                {user?.user_metadata?.full_name || 'User'}
              </p>
              <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to="/settings" className="flex items-center cursor-pointer w-full">
              <NavIconSettings className="mr-2 h-4 w-4" />
              <span>Settings</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive cursor-pointer"
            onSelect={() => handleSignOut()}
          >
            <LogOut className="mr-2 h-4 w-4" />
            <span>Sign out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  return (
    <TooltipProvider delayDuration={300}>
      <div data-app-shell className="flex h-dvh bg-background">
        {/* ─── Desktop Sidebar ─────────────────────────────────────────── */}
        {!onAdminRoute && (
          <aside
            className="fixed left-0 top-0 bottom-0 z-50 hidden lg:flex flex-col bg-sidebar border-r border-sidebar-border overflow-hidden"
            style={{
              width: sidebarWidth,
              transition: 'width 0.2s ease',
            }}
          >
            <div className={`flex-1 flex flex-col ${expanded ? 'px-3 py-4' : 'px-2 py-4'} overflow-hidden`}>
              {renderSidebarContent()}
            </div>
          </aside>
        )}

        {/* ─── Mobile Header ───────────────────────────────────────────── */}
        <header className="fixed top-0 left-0 right-0 h-[calc(3.5rem+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)] bg-sidebar border-b border-sidebar-border flex items-center justify-between px-4 lg:hidden z-40">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 rounded-lg hover:bg-sidebar-foreground/10 transition-colors"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            >
              {mobileOpen ? (
                <NavIconClose className="w-5 h-5 text-sidebar-foreground" />
              ) : (
                <NavIconMenu className="w-5 h-5 text-sidebar-foreground/70" />
              )}
            </button>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[hsl(var(--brand-orange))] to-[hsl(var(--brand-orange-light))] flex items-center justify-center">
                <span className="font-serif italic text-xs text-white">A</span>
              </div>
              <span className="font-serif italic text-base text-sidebar-foreground">ALFIE AI</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell variant="header" />
            <ThemeToggleSimple />
          </div>
        </header>

        {/* ─── Mobile Sidebar Drawer ───────────────────────────────────── */}
        <AnimatePresence>
          {mobileOpen && (
            <>
              {/* Backdrop — z-[60] sits above any open Dialog (z-50) so the
                  drawer's darkened background overlays them too. */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 bg-black/50 z-[60] lg:hidden"
                onClick={() => setMobileOpen(false)}
              />
              {/* Drawer — full viewport width on mobile so every nav label
                  ("Home", "Community", "Learn", "Account" etc.) is readable.
                  The old 240px width squashed labels to truncation, and on
                  /alfie routes where `expanded` is false (because our
                  per-route collapse effect runs), renderNavItem was rendering
                  icons only. Mobile drawer now forces labels regardless. */}
              <motion.aside
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
                className="fixed inset-x-0 top-[calc(3.5rem+env(safe-area-inset-top))] bottom-0 z-[60] flex flex-col bg-sidebar border-r border-sidebar-border lg:hidden overflow-hidden"
              >
                <div className="flex-1 flex flex-col px-4 py-4 overflow-hidden">
                  {/* Skip logo on mobile — it's in the header */}
                  {/* ALFIE Chat — navigates to full page */}
                  {!isAdvisor && renderNavItem(ALFIE_ITEM, true, true)}

                  {/* Divider */}
                  <div className="h-px bg-sidebar-border mx-2 my-1" />

                  {/* Main nav. It takes ALL the free height, as on desktop: a
                      second flex-1 spacer here split that height in half and
                      clipped the list at People on a phone, hiding Tracking,
                      Projects and Streams behind an inner scroll. */}
                  <nav className="flex-1 min-h-0 flex flex-col gap-0.5 overflow-y-auto">
                    {navItems.map((item) => renderNavItem(item, false, true))}
                  </nav>

                  {/* Divider */}
                  <div className="h-px bg-sidebar-border mx-2 my-1" />

                  {/* Theme toggle */}
                  <div className="flex items-center justify-between px-2.5 py-1.5">
                    <span className="text-[11px] text-sidebar-foreground/70 font-medium">Theme</span>
                    <ThemeToggle size="sm" />
                  </div>

                  {/* Account (Settings lives inside the dropdown) */}
                  <DropdownMenu open={accountMenuMobile.open} onOpenChange={accountMenuMobile.onOpenChange}>
                    <DropdownMenuTrigger asChild>
                      <button
                        {...accountMenuMobile.triggerProps}
                        aria-label="User menu"
                        className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all duration-150 min-h-[40px] text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-foreground/10 outline-none w-full"
                      >
                        <CachedAvatar
                          userId={user?.id}
                          url={profile?.avatar_url ?? user?.user_metadata?.avatar_url ?? null}
                          name={user?.user_metadata?.full_name ?? user?.email ?? null}
                          className="h-6 w-6 flex-shrink-0 rounded-full border border-sidebar-border"
                          fallbackClassName="bg-[hsl(var(--brand-orange))]/20 text-[hsl(var(--brand-orange))] text-[10px] uppercase"
                        />
                        <span className="text-xs whitespace-nowrap truncate">Account</span>
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" side="top" className="w-56 mb-2 z-[70]">
                      <DropdownMenuLabel className="font-normal">
                        <div className="flex flex-col space-y-1">
                          <p className="text-sm font-medium leading-none">
                            {user?.user_metadata?.full_name || 'User'}
                          </p>
                          <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/settings" className="flex items-center cursor-pointer w-full">
                          <NavIconSettings className="mr-2 h-4 w-4" />
                          <span>Settings</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive cursor-pointer"
                        onSelect={() => handleSignOut()}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>Sign out</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        {/* ─── Main Content ────────────────────────────────────────────── */}
        {/*
          Only the left margin moves now: it tracks the sidebar's width at lg+
          and is 0 below it. The old marginRight belonged to the docked Alfie
          drawer, which the floating widget replaced.
        */}
        <main
          // `transition-[margin-left]`, not `transition-[margin]`: the right
          // margin stopped moving when the docked Alfie panel went, and only
          // the left one still tracks the sidebar's own 200ms width animation.
          className="flex-1 pt-[calc(3.5rem+env(safe-area-inset-top))] lg:pt-0 bg-background transition-[margin-left] duration-200 flex flex-col min-h-0 overflow-hidden"
          style={{
            marginLeft: `var(--sidebar-width, 0px)`,
          }}
        >
          <style>{`
            @media (min-width: 1024px) {
              :root { --sidebar-width: ${sidebarWidth}px; }
            }
            @media (max-width: 1023px) {
              :root { --sidebar-width: 0px; }
            }
          `}</style>
          {/* On /alfie routes, drop the standard page padding and let Alfie
              render edge-to-edge in the main slot. Other routes keep the
              normal px/py wrapper padding. */}
          <div
            className={`flex-1 flex flex-col min-h-0 ${
              onFullBleedRoute ? '' : 'px-3 sm:px-4 lg:px-6 py-4 overflow-y-auto'
            }${
              // The floating launcher occupies the bottom-right corner, so the
              // padded scroller reserves it: the last row of a table or a
              // right-aligned pagination control can always scroll clear.
              showAlfieWidget && !onFullBleedRoute ? ' pb-24' : ''
            }`}
          >
            {/* Opt-in banners render on EVERY route. Full-bleed routes
                (community, alfie, events) have no wrapper padding — and they
                are exactly where community members live, so hiding the push
                banner there kept the people who most needed it from ever
                seeing it. `empty:hidden` collapses the padded strip when
                neither banner has anything to show. */}
            {onFullBleedRoute ? (
              <div className="px-3 pt-3 sm:px-4 lg:px-6 empty:hidden">
                <DunningWarning />
                <InstallPwaBanner />
                <EnableNotificationsBanner />
              </div>
            ) : (
              <>
                <DunningWarning />
                <InstallPwaBanner />
                <EnableNotificationsBanner />
              </>
            )}
            {/* Speedies (F-129): the full-screen player over whatever page is open. Children only while the flag is off. */}
            <SpeedyPlayerProvider>{children}</SpeedyPlayerProvider>
          </div>
        </main>

        {/* ─── Floating Alfie widget (hidden on /alfie page) ───────────────
            Also hidden for confirmed-Free viewers: Free has no alfie_chat
            entitlement, and this widget is the one Alfie entry point with no
            gate (the /alfie route guard + sidebar padlock both check it).
            `isFree` is only true on a settled read, so entitled users never
            see a flicker; the alfie-chat edge function is the authoritative
            backstop either way. */}
        {showAlfieWidget && <AlfieWidget />}
      </div>
    </TooltipProvider>
  );
}
